import { useCallback, useMemo, useState } from 'react';
import AudioPlayer from 'react-h5-audio-player';
import { styled } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import { ExternalLink } from '@components/Link';
import { Typography } from '@components/Typography';
import MarkdownRenderer, { isMarkdownContent } from '@components/MarkdownRenderer';
import { useTranslation } from '@hooks';
import { renderTextWithLinks } from '@helpers/parse-link';
import { parseMessageMetadata } from '@helpers/inbox';
import { MessageContentType, MessageNoticeKey, MessageStatus } from '@constants/inbox';
import { inboxService } from '@services/inbox';
import DOMPurify from 'dompurify';
import { FileIcon } from 'assets/images/icons';

import 'react-h5-audio-player/lib/styles.css';

DOMPurify.addHook('afterSanitizeAttributes', node => {
    if (node.tagName === 'A') {
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer');
    }
});

const EMAIL_SANITIZE_CONFIG = {
    ALLOWED_TAGS: [
        'p',
        'br',
        'b',
        'i',
        'u',
        'strong',
        'em',
        'del',
        's',
        'a',
        'ul',
        'ol',
        'li',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'blockquote',
        'pre',
        'code',
        'img',
        'table',
        'thead',
        'tbody',
        'tr',
        'td',
        'th',
        'div',
        'span',
        'hr',
    ],
    ALLOWED_ATTR: ['href', 'src', 'alt', 'width', 'height'],
    ADD_ATTR: ['target', 'rel'],
    FORCE_BODY: true,
};

const HtmlEmailContent = styled('div')({
    wordBreak: 'break-word',
    fontFamily: 'Rubik, sans-serif',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '0.875rem',
    lineHeight: '16px',
    letterSpacing: '0.25px',
    color: 'var(--text-primary)',
    '& h1, & h2, & h3, & h4, & h5, & h6, & p, & ul, & ol, & pre, & blockquote': {
        margin: '0 0 8px 0',
        '&:last-child': {
            marginBottom: 0,
        },
    },
    '& h1, & h2, & h3, & h4, & h5, & h6': {
        fontSize: '0.875rem',
        fontWeight: 500,
    },
    '& strong': {
        fontWeight: 500,
    },
    '& ul, & ol': {
        paddingLeft: '20px',
    },
    '& blockquote': {
        borderLeft: '4px solid #1F4C5C',
        paddingLeft: '16px',
        color: 'var(--text-secondary)',
    },
    '& code': {
        backgroundColor: 'var(--divider)',
        padding: '2px 4px',
        borderRadius: '8px',
        fontFamily: 'monospace',
    },
    '& pre': {
        backgroundColor: 'rgba(0, 0, 0, 0.08)',
        padding: '16px',
        borderRadius: '8px',
        overflowX: 'auto',
        '& code': {
            backgroundColor: 'transparent',
            padding: 0,
        },
    },
    '& del, & s': {
        textDecoration: 'line-through',
    },
    '& a': {
        color: 'var(--text-primary)',
    },
    '& img': {
        maxWidth: '100%',
        height: 'auto',
    },
});

const EmailMetaHeader = styled('div')({
    marginBottom: 8,
    paddingBottom: 8,
    borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
    fontFamily: 'Rubik, sans-serif',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '0.75rem',
    lineHeight: 1.6,
    letterSpacing: '0.25px',
    color: 'var(--text-secondary)',
    '& span': {
        color: 'var(--text-primary)',
    },
});

const ImageBlurWrapper = styled('div')(() => ({
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 80,
    height: 60,
    backgroundColor: 'rgba(255, 255, 255, 0.4)',
    pointerEvents: 'none',
    '& .MuiCircularProgress-root': {
        color: 'rgba(0, 0, 0, 0.38) !important',
    },
}));

const StyledMarkdownRenderer = styled(MarkdownRenderer)({
    maxWidth: '100%',
});

const StyledAudioPlayer = styled(AudioPlayer)({
    width: 250,
    boxShadow: 'none',
    border: 'none',
    backgroundColor: 'transparent',
    padding: 0,
    '& .rhap_time': {
        color: 'var(--text-primary)',
        fontSize: 12,
        fontFamily: 'Rubik, sans-serif',
        width: 34,
    },
    '& .rhap_progress-indicator': {
        width: 16,
        height: 16,
        top: -6,
    },
    '& .rhap_controls-section': {
        flex: 1,
    },
    '& .rhap_play-pause-button': {
        width: 32,
        height: 32,
        fontSize: 32,
    },
    '& svg': {
        opacity: 1,
        '& path': {
            fill: '#868686 !important',
        },
    },
});

const QuotedTextToggle = styled('button')({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '2px 8px',
    marginTop: 8,
    border: '1px solid rgba(0, 0, 0, 0.12)',
    borderRadius: 12,
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontFamily: 'Rubik, sans-serif',
    fontSize: '0.75rem',
    color: 'var(--text-secondary)',
    '&:hover:not(:disabled)': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
    '&:disabled': {
        cursor: 'default',
        opacity: 0.5,
    },
});

const MessageContent = ({ data, isOutgoing, isEmail }) => {
    const [isStoryError, setIsStoryError] = useState(false);
    const [showQuoted, setShowQuoted] = useState(false);
    const [originalHtml, setOriginalHtml] = useState(null);
    const [loadingOriginal, setLoadingOriginal] = useState(false);

    const { t } = useTranslation();

    const emailMeta = useMemo(() => (isEmail ? parseMessageMetadata(data.metadata) : null), [isEmail, data.metadata]);

    const hasQuotedContent = Boolean(emailMeta?.hasOriginalHtml);

    const handleToggleQuoted = useCallback(async () => {
        if (showQuoted) {
            setShowQuoted(false);
            return;
        }

        if (originalHtml) {
            setShowQuoted(true);
            return;
        }

        if (!data.id) return;

        setLoadingOriginal(true);
        try {
            const response = await inboxService.getOriginalHtml(data.id);
            if (response.htmlContent) {
                setOriginalHtml(response.htmlContent);
                setShowQuoted(true);
            }
        } catch {
            // Fetch failed — keep collapsed
        } finally {
            setLoadingOriginal(false);
        }
    }, [showQuoted, originalHtml, data.id]);

    return data.status === MessageStatus.Deleted ? (
        <Typography variant="body2" sx={{ fontStyle: 'italic !important' }}>
            {t('InboxPage.messageDeleted')}
        </Typography>
    ) : (
        <>
            {emailMeta &&
            (emailMeta.subject || emailMeta.ccAddresses?.length > 0 || emailMeta.bccAddresses?.length > 0) ? (
                <EmailMetaHeader>
                    {emailMeta.subject ? (
                        <div>
                            {t('common.subject')}: <span>{emailMeta.subject}</span>
                        </div>
                    ) : null}
                    {emailMeta.ccAddresses?.length > 0 ? (
                        <div>
                            {t('common.cc')}: <span>{emailMeta.ccAddresses.join(', ')}</span>
                        </div>
                    ) : null}
                    {emailMeta.bccAddresses?.length > 0 ? (
                        <div>
                            {t('common.bcc')}: <span>{emailMeta.bccAddresses.join(', ')}</span>
                        </div>
                    ) : null}
                </EmailMetaHeader>
            ) : null}
            {data.contentType === MessageContentType.Text ? (
                isMarkdownContent(data.content) ? (
                    <StyledMarkdownRenderer content={data.content} />
                ) : (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {renderTextWithLinks(data.content)}
                    </Typography>
                )
            ) : null}
            {data.contentType === MessageContentType.Image ? (
                <ExternalLink
                    href={data.content}
                    underline="none"
                    sx={{
                        position: 'relative',
                        pointerEvents: isOutgoing && data.status === MessageStatus.Sent ? 'none' : 'auto',
                    }}
                >
                    {data.status === MessageStatus.Sent ? (
                        <ImageBlurWrapper>
                            <CircularProgress />
                        </ImageBlurWrapper>
                    ) : null}
                    <img width={80} height={60} style={{ objectFit: 'contain' }} src={data.content} alt="download" />
                </ExternalLink>
            ) : null}
            {data.contentType === MessageContentType.StoryImage ? (
                !isStoryError ? (
                    <ExternalLink href={data.content} underline="none">
                        <img
                            width={80}
                            height={60}
                            style={{ objectFit: 'contain' }}
                            onError={() => setIsStoryError(true)}
                            src={data.content}
                            alt="download"
                        />
                    </ExternalLink>
                ) : (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {t('InboxPage.storyNotAvailable')}
                    </Typography>
                )
            ) : null}
            {data.contentType === MessageContentType.Video ? (
                <ExternalLink href={data.content} underline="none" sx={{ display: 'flex' }}>
                    <video preload="metadata" style={{ width: 80, height: 60, maxWidth: '100%' }} src={data.content} />
                </ExternalLink>
            ) : null}
            {data.contentType === MessageContentType.StoryVideo ? (
                !isStoryError ? (
                    <ExternalLink href={data.content} underline="none" sx={{ display: 'flex' }}>
                        <video
                            preload="metadata"
                            style={{ width: 80, height: 60, maxWidth: '100%' }}
                            src={data.content}
                            onError={() => setIsStoryError(true)}
                        />
                    </ExternalLink>
                ) : (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {t('InboxPage.storyNotAvailable')}
                    </Typography>
                )
            ) : null}
            {data.contentType === MessageContentType.Audio ? (
                <StyledAudioPlayer
                    src={data.content}
                    preload="metadata"
                    layout="horizontal-reverse"
                    showJumpControls={false}
                    customAdditionalControls={[]}
                    customVolumeControls={[]}
                />
            ) : null}
            {data.contentType === MessageContentType.Html ? (
                <>
                    <HtmlEmailContent
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(
                                (showQuoted && originalHtml) ||
                                    emailMeta?.htmlContent ||
                                    (data.content || '').replace(/\n/g, '<br>'),
                                EMAIL_SANITIZE_CONFIG,
                            ),
                        }}
                    />
                    {hasQuotedContent ? (
                        <QuotedTextToggle onClick={handleToggleQuoted} disabled={loadingOriginal}>
                            {loadingOriginal
                                ? `\u2026`
                                : showQuoted
                                  ? `\u25B2 ${t('InboxPage.hideQuotedText')}`
                                  : `\u2026 ${t('InboxPage.showQuotedText')}`}
                        </QuotedTextToggle>
                    ) : null}
                </>
            ) : null}
            {data.contentType === MessageContentType.File ? (
                <ExternalLink href={data.content} underline="none" sx={{ display: 'flex', alignItems: 'flex-end' }}>
                    <FileIcon />
                    <Typography
                        variant="body2"
                        sx={{ display: 'inline-block', marginLeft: '4px', color: 'var(--text-primary)' }}
                    >
                        {t('common.file')}
                    </Typography>
                </ExternalLink>
            ) : null}
            {MessageNoticeKey[data.contentType] ? (
                <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                    {t(MessageNoticeKey[data.contentType])}
                </Typography>
            ) : null}
        </>
    );
};

export default MessageContent;
